import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useWS } from "@/contexts/WebSocketContext";
import { toast } from "sonner";
import { Plus, Minus, X, Send, FileText, ArrowLeft, AlertTriangle, AlarmClock, CheckCircle2, ChefHat, Users, Printer, AlertOctagon, Mic, Square, Volume2, Loader2 } from "lucide-react";
import { tryDirectPrint } from "@/lib/fastPrint";

const COURSES = [
    { id: "antipasto", label: "Antipasto" },
    { id: "primo", label: "Primo" },
    { id: "secondo", label: "Secondo" },
    { id: "contorno", label: "Contorno" },
    { id: "pizza", label: "Pizza" },
    { id: "dolce", label: "Dolce" },
    { id: "bevanda", label: "Bevanda" },
];

const OrderEditor = () => {
    const { tableId } = useParams();
    const navigate = useNavigate();
    const [table, setTable] = useState(null);
    const [order, setOrder] = useState(null);
    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [activeCat, setActiveCat] = useState(null);
    const [noteFor, setNoteFor] = useState(null);
    const [noteText, setNoteText] = useState("");
    const [courseFor, setCourseFor] = useState(null);
    const [roundFor, setRoundFor] = useState(null);
    const [extrasFor, setExtrasFor] = useState(null);  // item per cui editare extras (null se chiuso)
    const [extrasDraft, setExtrasDraft] = useState([]); // [{id,label,price}]
    const [extraLabel, setExtraLabel] = useState("");
    const [extraPrice, setExtraPrice] = useState("");
    const [coversPrompt, setCoversPrompt] = useState(false);
    const [coversPromptMode, setCoversPromptMode] = useState("opening"); // 'opening' (nuovo tavolo, mostra annulla) | 'edit' (modifica esistente)
    const [coversInput, setCoversInput] = useState(2);
    const [coversChildrenInput, setCoversChildrenInput] = useState(0);
    const [coverDisabledInput, setCoverDisabledInput] = useState(false);
    const [coverExtraInput, setCoverExtraInput] = useState(""); // coperto extra €/persona se menu non lo prevede
    const [addExtraCover, setAddExtraCover] = useState(false); // toggle visuale per attivare campo extra
    const [coperto, setCoperto] = useState({ enabled: false, unit: 0, childEnabled: false, childUnit: 0 });
    const [voidFor, setVoidFor] = useState(null);          // item da annullare dopo invio cucina
    const [voidReason, setVoidReason] = useState("");
    const [voidBusy, setVoidBusy] = useState(false);
    // "ALTRO" — articolo libero (non in menu)
    const [freeName, setFreeName] = useState("");
    const [freePrice, setFreePrice] = useState("");
    const [freeQty, setFreeQty] = useState(1);
    const [freeDept, setFreeDept] = useState("cucina");
    const [freeCourse, setFreeCourse] = useState("primo");
    const [freeNotes, setFreeNotes] = useState("");
    const [freeBusy, setFreeBusy] = useState(false);
    const FREE_CAT = "__altro__";
    const { user } = useAuth() || {};
    const canVoid = user?.role === "admin" || user?.role === "cashier";

    // ⚠️ Stato STAMPA dell'ultimo invio:
    //   "idle"     → nessun invio recente
    //   "printing" → invio appena fatto, in attesa di ACK stampa
    //   "ok"       → tutti i job stampati con successo OR solo video_only (badge verde)
    //   "warning"  → in MIXED mode, video OK ma stampa fallita → NON bloccante, click=ristampa, ma si può andare avanti
    //   "failed"   → in PRINT_ONLY, stampa fallita → BLOCCANTE, click=ristampa obbligatoria
    // I job vengono tracciati in `printJobsRef` mappa job_id → {status, destination, video_visible}.
    const [printState, setPrintState] = useState("idle");
    const [lastPrintError, setLastPrintError] = useState("");
    const printJobsRef = useRef(new Map()); // job_id → {status, destination, printer_name, video_visible}
    const printStateTimerRef = useRef(null);
    const printingTimeoutRef = useRef(null); // ⚠️ Safety timeout per stati "printing" che non ricevono mai ACK
    const { subscribe } = useWS() || {};

    // Aggiorna `printState` in base allo stato aggregato dei job tracciati
    const recomputePrintState = () => {
        const jobs = Array.from(printJobsRef.current.values());
        if (jobs.length === 0) {
            setPrintState("idle");
            return;
        }
        const failedJobs = jobs.filter(j => j.status === "failed" || j.status === "missed");
        const anyPending = jobs.some(j => j.status === "pending");
        const anyFailed = failedJobs.length > 0;
        if (anyFailed) {
            // ⚠️ Tutti i job FAILED sono video_visible (cioè in mixed)? → solo WARNING non bloccante.
            // Almeno uno è !video_visible (print_only)? → FAILED bloccante.
            const allFailedAreVideoVisible = failedJobs.every(j => j.video_visible === true);
            const firstFail = failedJobs[0];
            setLastPrintError(firstFail?.error || "Stampa non confermata");
            if (allFailedAreVideoVisible) {
                setPrintState("warning"); // giallo, non bloccante
            } else {
                setPrintState("failed"); // rosso, bloccante
            }
            if (printingTimeoutRef.current) { clearTimeout(printingTimeoutRef.current); printingTimeoutRef.current = null; }
        } else if (anyPending) {
            setPrintState("printing");
            // ⚠️ Timer di FEEDBACK BREVE: se dopo 8s non arriva l'ACK, mostriamo "failed" (o warning
            // a seconda della modalità) senza aspettare i 45s del backend.
            if (printingTimeoutRef.current) clearTimeout(printingTimeoutRef.current);
            printingTimeoutRef.current = setTimeout(() => {
                const stillPending = Array.from(printJobsRef.current.values()).some(j => j.status === "pending");
                if (stillPending) {
                    console.warn("[OrderEditor] 8s senza ACK → mostra failed/warning");
                    printJobsRef.current.forEach((v, k) => {
                        if (v.status === "pending") {
                            printJobsRef.current.set(k, {
                                ...v,
                                status: "missed",
                                error: "Stampa non confermata. Verifica stampante o PC cassa.",
                            });
                        }
                    });
                    // ricalcola con la nuova mappa
                    recomputePrintState();
                }
                printingTimeoutRef.current = null;
            }, 8000);
        } else {
            setPrintState("ok");
            if (printingTimeoutRef.current) { clearTimeout(printingTimeoutRef.current); printingTimeoutRef.current = null; }
            // Reset automatico dopo 8s (così il bottone torna idle)
            if (printStateTimerRef.current) clearTimeout(printStateTimerRef.current);
            printStateTimerRef.current = setTimeout(() => {
                printJobsRef.current.clear();
                setPrintState("idle");
                setLastPrintError("");
            }, 8000);
        }
    };

    // WS listener per print_status_changed (solo job del nostro ultimo invio)
    useEffect(() => {
        if (!subscribe) return;
        const unsub = subscribe((msg) => {
            if (msg?.type !== "print_status_changed") return;
            if (!msg.job_id || !msg.order_id) return;
            if (order?.id && msg.order_id !== order.id) return; // solo questo ordine
            if (!printJobsRef.current.has(msg.job_id)) return;
            const prev = printJobsRef.current.get(msg.job_id) || {};
            printJobsRef.current.set(msg.job_id, {
                ...prev,
                status: msg.status,
                destination: msg.destination,
                printer_name: msg.printer_name,
                error: msg.error,
                // Aggiorna video_visible se il server lo fornisce (può migliorare il flag iniziale)
                video_visible: msg.video_visible !== undefined ? msg.video_visible : prev.video_visible,
            });
            recomputePrintState();
        });
        return unsub;
    }, [subscribe, order?.id]);

    // Cleanup timer alla smontatura del componente
    useEffect(() => {
        return () => {
            if (printStateTimerRef.current) clearTimeout(printStateTimerRef.current);
            if (printingTimeoutRef.current) clearTimeout(printingTimeoutRef.current);
        };
    }, []);

    const init = async () => {
        try {
            const [tablesRes, catsRes, itemsRes, activeMenuRes] = await Promise.all([
                api.get("/tables"),
                api.get("/menu/categories"),
                api.get("/menu/items"),
                api.get("/menus/active").catch(() => ({ data: {} })),
            ]);
            const t = tablesRes.data.find((x) => x.id === tableId);
            setTable(t);
            setCategories(catsRes.data);
            setActiveCat(catsRes.data[0]?.id);
            setItems(itemsRes.data);
            const am = activeMenuRes.data || {};
            setCoperto({
                enabled: !!am.coperto_enabled,
                unit: parseFloat(am.coperto_amount || 0),
                childEnabled: !!am.coperto_child_enabled,
                childUnit: parseFloat(am.coperto_child_amount || 0),
            });

            // create or get current order
            const { data: o } = await api.post("/orders", { table_id: tableId });
            setOrder(o);
            // SEMPRE chiedi il numero di coperti all'apertura di un nuovo ordine (anche se il menu non prevede coperto)
            if (!o.covers || o.covers === 0) {
                setCoversInput(t?.seats || 2);
                setCoversChildrenInput(0);
                setCoverDisabledInput(!!o.cover_charge_disabled);
                setCoverExtraInput(o.cover_charge_extra ? String(o.cover_charge_extra) : "");
                setAddExtraCover(!!(o.cover_charge_extra && o.cover_charge_extra > 0));
                setCoversPromptMode("opening");
                setCoversPrompt(true);
            }
        } catch (e) { toast.error(formatError(e)); }
    };

    useEffect(() => { init(); }, [tableId]);

    // WS listener: aggiorna in tempo reale i piatti TERMINATI / RIATTIVATI
    useEffect(() => {
        if (!subscribe) return;
        const unsub = subscribe((msg) => {
            if (msg?.type === "menu_item_updated" && msg.item) {
                setItems((prev) => prev.map((i) => i.id === msg.item.id ? { ...i, ...msg.item } : i));
            }
            if (msg?.type === "menu_activated") {
                // reload menu items quando l'admin cambia il menu attivo
                api.get("/menu/items").then(({ data }) => setItems(data)).catch(() => {});
                api.get("/menu/categories").then(({ data }) => { setCategories(data); setActiveCat(data[0]?.id); }).catch(() => {});
            }
        });
        return unsub;
    }, [subscribe]);

    const reloadOrder = async () => {
        if (!order) return;
        const { data } = await api.get(`/orders/${order.id}`);
        setOrder(data);
    };

    const addItem = async (menuItem) => {
        try {
            await api.post(`/orders/${order.id}/items`, { menu_item_id: menuItem.id, quantity: 1 });
            await reloadOrder();
        } catch (e) { toast.error(formatError(e)); }
    };

    const removeItem = async (itemId) => {
        await api.delete(`/orders/${order.id}/items/${itemId}`);
        await reloadOrder();
    };

    const addFreeItem = async () => {
        const nm = (freeName || "").trim();
        if (!nm) { toast.error("Inserisci il nome dell'articolo"); return; }
        const pr = parseFloat((freePrice || "").replace(",", "."));
        if (!isFinite(pr) || pr < 0) { toast.error("Prezzo non valido"); return; }
        const q = Math.max(1, parseInt(freeQty, 10) || 1);
        setFreeBusy(true);
        try {
            await api.post(`/orders/${order.id}/items/free`, {
                name: nm,
                price: pr,
                quantity: q,
                course: freeCourse,
                department: freeDept,
                notes: (freeNotes || "").trim(),
                vat_rate: 10,
            });
            toast.success(`"${nm}" aggiunto al conto`);
            setFreeName("");
            setFreePrice("");
            setFreeQty(1);
            setFreeNotes("");
            await reloadOrder();
        } catch (e) { toast.error(formatError(e)); }
        setFreeBusy(false);
    };

    const VOID_PRESETS = ["Errore battitura", "Cliente rifiutato", "Cortesia / Omaggio", "Cambio piatto", "Difetto cucina"];

    const openVoid = (item) => {
        setVoidFor(item);
        setVoidReason("");
    };

    const confirmVoid = async () => {
        const reason = (voidReason || "").trim();
        if (!reason) {
            toast.error("Specifica il motivo della cancellazione");
            return;
        }
        setVoidBusy(true);
        try {
            await api.delete(`/orders/${order.id}/items/${voidFor.id}`, { params: { reason } });
            toast.success(`"${voidFor.name}" rimosso dal conto`);
            setVoidFor(null);
            setVoidReason("");
            await reloadOrder();
        } catch (e) {
            toast.error(formatError(e));
        }
        setVoidBusy(false);
    };

    const changeQty = async (item, delta) => {
        const newQty = item.quantity + delta;
        if (newQty < 1) return removeItem(item.id);
        await api.patch(`/orders/${order.id}/items/${item.id}`, { quantity: newQty });
        await reloadOrder();
    };

    const saveNote = async () => {
        if (!noteFor) return;
        await api.patch(`/orders/${order.id}/items/${noteFor.id}`, { notes: noteText });
        setNoteFor(null);
        setNoteText("");
        await reloadOrder();
    };

    const setCourse = async (item, course) => {
        await api.patch(`/orders/${order.id}/items/${item.id}`, { course });
        setCourseFor(null);
        await reloadOrder();
    };

    const setRound = async (item, round) => {
        try {
            await api.patch(`/orders/${order.id}/items/${item.id}/round`, { course_round: round });
            setRoundFor(null);
            await reloadOrder();
        } catch (e) { toast.error(formatError(e)); }
    };

    const openExtras = (item) => {
        setExtrasFor(item);
        setExtrasDraft([...(item.extras || [])]);
        setExtraLabel("");
        setExtraPrice("");
    };

    const addExtraToDraft = () => {
        const lbl = extraLabel.trim();
        if (!lbl) {
            toast.info("Inserisci la descrizione dell'aggiunta");
            return;
        }
        if (extrasDraft.length >= 10) {
            toast.error("Massimo 10 aggiunte per piatto");
            return;
        }
        const p = Math.max(0, parseFloat((extraPrice + "").replace(",", ".")) || 0);
        setExtrasDraft((d) => [...d, { id: Math.random().toString(36).slice(2, 10), label: lbl, price: p }]);
        setExtraLabel("");
        setExtraPrice("");
    };

    const removeExtraFromDraft = (id) => {
        setExtrasDraft((d) => d.filter((e) => e.id !== id));
    };

    const saveExtras = async () => {
        try {
            await api.patch(`/orders/${order.id}/items/${extrasFor.id}/extras`, { extras: extrasDraft });
            setExtrasFor(null);
            setExtrasDraft([]);
            await reloadOrder();
            toast.success("Aggiunte salvate");
        } catch (e) { toast.error(formatError(e)); }
    };

    const saveCovers = async () => {
        const n = Math.max(1, parseInt(coversInput, 10) || 0);
        const nc = Math.max(0, parseInt(coversChildrenInput, 10) || 0);
        // Extra coperto: applicato solo se menu non prevede coperto e l'operatore ha attivato l'opzione
        let extra = 0;
        if (!coperto.enabled && addExtraCover) {
            const v = parseFloat((coverExtraInput || "").replace(",", "."));
            if (!isFinite(v) || v < 0) {
                toast.error("Importo coperto non valido");
                return;
            }
            if (v > 50) {
                toast.error("Importo coperto troppo elevato (max € 50)");
                return;
            }
            extra = Math.round(v * 100) / 100;
        }
        try {
            await api.patch(`/orders/${order.id}/covers`, {
                covers: n,
                covers_children: nc,
                cover_charge_disabled: !!coverDisabledInput,
                cover_charge_extra: extra,
            });
            setCoversPrompt(false);
            await reloadOrder();
            const parts = [];
            parts.push(`${n} ${n === 1 ? "adulto" : "adulti"}`);
            if (nc > 0) parts.push(`${nc} ${nc === 1 ? "bambino" : "bambini"}`);
            if (coperto.enabled && coverDisabledInput) parts.push("senza coperto");
            if (!coperto.enabled && extra > 0) parts.push(`coperto € ${extra.toFixed(2)}/pers`);
            toast.success(`Coperti: ${parts.join(" + ")}`);
        } catch (e) { toast.error(formatError(e)); }
    };

    const cancelTableOpening = async () => {
        // Annulla l'apertura di un tavolo appena aperto: cancella l'ordine vuoto e libera il tavolo
        if (!order) return;
        const hasItems = (order.items || []).some((it) => it.status !== "annullato");
        if (hasItems) {
            toast.error("Il tavolo ha già piatti inseriti. Non si può annullare l'apertura.");
            setCoversPrompt(false);
            return;
        }
        if (!window.confirm("Annullare l'apertura del tavolo? Il tavolo tornerà libero.")) return;
        try {
            await api.post(`/orders/${order.id}/cancel-empty`);
            toast.success("Apertura tavolo annullata");
            setCoversPrompt(false);
            navigate("/mappa-sala");
        } catch (e) { toast.error(formatError(e)); }
    };

    const [sending, setSending] = useState(false);
    const sendToKitchen = async () => {
        if (sending) return;
        setSending(true);
        try {
            const { data } = await api.post(`/orders/${order.id}/send`);
            if (data?.duplicate) {
                toast.info("Nessun nuovo item da inviare");
            } else {
                toast.success("Comanda inviata", { duration: 1500 });
                // ⚠️ Inizializza il tracking dei job: marca tutti come "pending".
                // Il bottone Invia mostra spinner finché tutti i job arrivano in delivered.
                const dps = data?.direct_payloads || [];
                const errs = data?.print_errors || [];
                printJobsRef.current.clear();

                // Se ci sono ERRORI IMMEDIATI dal backend (TCP offline, no printer, etc.)
                // li mostriamo SUBITO come rosso/giallo lampeggiante (no attesa ACK)
                if (errs.length > 0) {
                    errs.forEach((e, idx) => {
                        const pseudoId = `imm-err-${idx}-${Date.now()}`;
                        printJobsRef.current.set(pseudoId, {
                            status: "failed",
                            destination: e.destination,
                            printer_name: e.printer_name || "",
                            error: e.message || "Errore stampa",
                            video_visible: e.video_visible === true, // mixed mode = video OK
                        });
                    });
                }

                if (dps.length > 0) {
                    dps.forEach((dp) => {
                        const j = dp?.job;
                        if (j?.id) {
                            printJobsRef.current.set(j.id, {
                                status: "pending",
                                destination: j.destination,
                                printer_name: dp?.printer_name || "",
                                // I direct_payloads sono SOLO per print_only/mixed.
                                // Se la modalità fosse video_only, nessun payload arrivava.
                                // Per i pending non sappiamo ancora la modalità → assumiamo mixed
                                // (defensiva: in caso di timeout sarà warning non bloccante).
                                video_visible: true,
                            });
                        }
                    });
                    recomputePrintState();
                    tryDirectPrint(dps).then(stats => {
                        if (stats.ok > 0) console.log("[OrderEditor] fast-print:", stats);
                    }).catch(e => console.warn("fast-print:", e));
                } else if (errs.length > 0) {
                    recomputePrintState();
                } else {
                    // Nessuna stampante configurata o solo video_only: stato ok diretto
                    setPrintState("ok");
                    if (printStateTimerRef.current) clearTimeout(printStateTimerRef.current);
                    printStateTimerRef.current = setTimeout(() => setPrintState("idle"), 4000);
                }
            }
            await reloadOrder();
        } catch (e) { toast.error(formatError(e)); }
        finally { setSending(false); }
    };

    // Ristampa SOLO i job che hanno fallito (rosso lampeggiante)
    const reprintFailed = async () => {
        if (!order?.id) return;
        const failedDest = Array.from(printJobsRef.current.values())
            .filter(j => j.status === "failed" || j.status === "missed")
            .map(j => j.destination)
            .filter(Boolean);
        const uniqueDests = [...new Set(failedDest)];
        if (uniqueDests.length === 0) return;
        setPrintState("printing");
        try {
            const { data } = await api.post(`/orders/${order.id}/reprint`,
                { destinations: uniqueDests }
            );
            if (data?.duplicate) {
                toast.info("Ristampa già in corso, attendi…");
                return;
            }
            // Reset tracking sui NUOVI job
            printJobsRef.current.clear();
            const dps = data?.direct_payloads || [];
            dps.forEach((dp) => {
                const j = dp?.job;
                if (j?.id) {
                    printJobsRef.current.set(j.id, {
                        status: "pending",
                        destination: j.destination,
                        printer_name: dp?.printer_name || "",
                    });
                }
            });
            tryDirectPrint(dps).catch(e => console.warn("reprint fast-print:", e));
            toast.success(`Ristampa avviata (${uniqueDests.join(", ")})`, { duration: 2000 });
        } catch (e) {
            toast.error(formatError(e));
            setPrintState("failed");
        }
    };

    // Dismiss WARNING senza ristampare: in modalità mixed, l'ordine è già a video
    // → l'utente può andare avanti (es. aggiungere altri piatti o passare ad altro tavolo)
    const dismissWarning = () => {
        printJobsRef.current.clear();
        setPrintState("idle");
        setLastPrintError("");
        toast.info("Ordine trasmesso a video. Stampa saltata.", { duration: 2000 });
    };

    const requestBill = async () => {
        try {
            await api.post(`/orders/${order.id}/request-bill`);
            toast.success("Conto richiesto. Cassa avvisata.");
            navigate("/mappa-sala");
        } catch (e) { toast.error(formatError(e)); }
    };

    const markDelivered = async (item) => {
        try {
            await api.post(`/orders/${order.id}/items/${item.id}/status`, { status: "consegnato" });
            await reloadOrder();
        } catch (e) { toast.error(formatError(e)); }
    };

    // Polling per stati cucina + tempi attesa
    // Tick 1s sul "now" per nascondere item consegnati dopo 10s senza necessità di refetch
    const [tickNow, setTickNow] = useState(Date.now());
    useEffect(() => {
        if (!order?.id) return;
        const id = setInterval(reloadOrder, 8000);
        const tickId = setInterval(() => setTickNow(Date.now()), 1000);
        return () => { clearInterval(id); clearInterval(tickId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order?.id]);

    // Filtro voci comanda: i piatti consegnati spariscono 10s dopo la consegna (allineato al KDS)
    const visibleItems = useMemo(() => {
        if (!order?.items) return [];
        return order.items.filter((it) => {
            if (it.status !== "consegnato") return true;
            if (!it.delivered_at) return true;
            const age = (tickNow - new Date(it.delivered_at).getTime()) / 1000;
            return age < 10;
        });
    }, [order, tickNow]);

    // Calcola alert massimo tra item in_cucina/in_preparazione (timer parte da sent_at)
    const orderAlert = useMemo(() => {
        if (!order) return { level: "none", maxMin: 0 };
        let maxMin = 0;
        for (const it of order.items) {
            if (["in_cucina", "in_preparazione"].includes(it.status) && it.sent_at) {
                const min = (tickNow - new Date(it.sent_at).getTime()) / 60000;
                if (min > maxMin) maxMin = min;
            }
        }
        const level = maxMin >= 30 ? "red" : maxMin >= 20 ? "yellow" : "none";
        return { level, maxMin: Math.floor(maxMin) };
    }, [order, tickNow]);

    const filteredItems = items.filter((i) => i.category_id === activeCat);
    const itemGross = (it) => {
        const exSum = (it.extras || []).reduce((s, e) => s + (parseFloat(e.price) || 0), 0);
        return (it.price + exSum) * it.quantity;
    };
    const total = useMemo(() => order?.items.reduce((s, it) => s + itemGross(it), 0) || 0, [order]);
    const pendingCount = order?.items.filter((it) => it.status === "in_attesa").length || 0;

    if (!order || !table) return <div className="text-elegant">Caricamento...</div>;

    return (
        <div className="space-y-6" data-testid="order-editor-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <button onClick={() => navigate("/mappa-sala")} className="flex items-center gap-2 text-elegant hover:text-brand" data-testid="back-button">
                    <ArrowLeft className="w-4 h-4" /> Torna alla sala
                </button>
                <div className="text-right">
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-1">Tavolo</div>
                    <h1 className="font-display text-4xl font-bold">
                        N° {table.number}
                        {order?.bis_label && (
                            <span className="ml-2 text-2xl text-amber-300 font-display" data-testid="bis-label">
                                ({order.bis_label})
                            </span>
                        )}
                    </h1>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Menu picker */}
                <div className="lg:col-span-2 bg-surface border border-[#1f2c44] rounded-lg overflow-hidden">
                    <div className="border-b border-[#1f2c44] overflow-x-auto overflow-y-hidden scroll-smooth [scrollbar-width:thin] relative">
                        <div className="flex flex-nowrap items-stretch min-w-max">
                            {categories.map((c) => (
                                <button
                                    key={c.id}
                                    onClick={() => setActiveCat(c.id)}
                                    data-testid={`category-${c.name.replace(/ /g, "-").toLowerCase()}`}
                                    className={`flex-shrink-0 px-5 py-4 text-sm uppercase tracking-wider whitespace-nowrap btn-touch border-r border-[#1f2c44]/70 last:border-r-0 ${
                                        activeCat === c.id ? "text-brand border-b-2 border-b-brand bg-brand-soft/30" : "text-elegant hover:bg-[#0e1626]"
                                    }`}
                                >
                                    {c.name}
                                </button>
                            ))}
                            {/* Tab speciale "ALTRO" — articolo libero non in menu */}
                            <button
                                onClick={() => setActiveCat(FREE_CAT)}
                                data-testid="category-altro"
                                className={`flex-shrink-0 px-5 py-4 text-sm uppercase tracking-wider whitespace-nowrap btn-touch border-l-2 border-l-amber-500/60 ${
                                    activeCat === FREE_CAT ? "text-amber-300 border-b-2 border-b-amber-500 bg-amber-900/20" : "text-amber-400/80 hover:bg-amber-900/10"
                                }`}
                            >
                                + Altro
                            </button>
                        </div>
                    </div>
                    {activeCat === FREE_CAT ? (
                        <div className="p-5 max-h-[600px] overflow-y-auto" data-testid="free-item-form">
                            <div className="bg-amber-900/15 border border-amber-500/40 rounded-lg p-4 mb-3">
                                <div className="flex items-start gap-2 mb-2">
                                    <Plus className="w-4 h-4 text-amber-400 mt-0.5" />
                                    <div>
                                        <div className="font-display text-lg font-bold text-amber-300">Articolo non in menu</div>
                                        <div className="text-[11px] text-amber-200/70">Aggiungi un piatto/bevanda con nome e prezzo a tua scelta. Verrà inviato alla destinazione (cucina/bar/pizzeria) selezionata.</div>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-elegant block mb-1">Nome articolo *</label>
                                    <input type="text" value={freeName} onChange={(e) => setFreeName(e.target.value)}
                                        placeholder="es. Bottiglia Vino della Casa, Affettato del giorno…"
                                        maxLength={120}
                                        data-testid="free-name-input"
                                        className="w-full h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-warm focus:border-amber-500 focus:outline-none" />
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-[10px] uppercase tracking-wider text-elegant block mb-1">Prezzo € *</label>
                                        <input type="number" min="0" step="0.50" value={freePrice}
                                            onChange={(e) => setFreePrice(e.target.value)}
                                            placeholder="es. 12.50"
                                            data-testid="free-price-input"
                                            className="w-full h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-right font-display text-lg text-warm focus:border-amber-500 focus:outline-none" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase tracking-wider text-elegant block mb-1">Quantità</label>
                                        <input type="number" min="1" max="50" value={freeQty}
                                            onChange={(e) => setFreeQty(e.target.value)}
                                            data-testid="free-qty-input"
                                            className="w-full h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-right font-display text-lg text-warm focus:border-amber-500 focus:outline-none" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-[10px] uppercase tracking-wider text-elegant block mb-1">Destinazione</label>
                                        <select value={freeDept} onChange={(e) => setFreeDept(e.target.value)}
                                            data-testid="free-dept-select"
                                            className="w-full h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-warm focus:border-amber-500 focus:outline-none">
                                            <option value="cucina">Cucina</option>
                                            <option value="bar">Bar</option>
                                            <option value="pizzeria">Pizzeria</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase tracking-wider text-elegant block mb-1">Portata</label>
                                        <select value={freeCourse} onChange={(e) => setFreeCourse(e.target.value)}
                                            data-testid="free-course-select"
                                            className="w-full h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-warm focus:border-amber-500 focus:outline-none">
                                            {COURSES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-elegant block mb-1">Note (opzionale)</label>
                                    <input type="text" value={freeNotes} onChange={(e) => setFreeNotes(e.target.value)}
                                        placeholder="es. ben cotto, senza ghiaccio…"
                                        maxLength={200}
                                        data-testid="free-notes-input"
                                        className="w-full h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-warm focus:border-amber-500 focus:outline-none" />
                                </div>

                                <button onClick={addFreeItem} disabled={freeBusy || !freeName.trim() || !freePrice}
                                    data-testid="free-add-btn"
                                    className="w-full h-12 bg-amber-500 hover:bg-amber-400 disabled:bg-amber-900/30 disabled:cursor-not-allowed text-deep font-bold rounded-md uppercase tracking-wider transition-colors flex items-center justify-center gap-2">
                                    <Plus className="w-4 h-4" />
                                    {freeBusy ? "Aggiungo..." : "Aggiungi al conto"}
                                </button>
                            </div>
                        </div>
                    ) : (
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[600px] overflow-y-auto">
                        {filteredItems.map((it) => {
                            const terminato = it.available === false;
                            return (
                                <button
                                    key={it.id}
                                    onClick={() => !terminato && addItem(it)}
                                    disabled={terminato}
                                    data-testid={`menu-item-${it.id}`}
                                    className={`text-left p-4 rounded-md border transition-colors btn-touch relative ${
                                        terminato
                                            ? "bg-deep border-red-600/40 cursor-not-allowed opacity-70"
                                            : "bg-surface-elevated border-[#1f2c44] hover:border-brand"
                                    }`}
                                >
                                    {terminato && (
                                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold bg-red-600 text-white animate-pulse" data-testid={`terminato-badge-${it.id}`}>
                                            TERMINATO
                                        </div>
                                    )}
                                    <div className="flex justify-between items-start gap-2">
                                        <div className="flex-1">
                                            <div className={`font-display font-semibold ${terminato ? "text-elegant line-through" : "text-warm"}`}>{it.name}</div>
                                            <div className="text-xs text-elegant mt-1">{it.description}</div>
                                            {it.allergens.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {it.allergens.slice(0, 3).map((a) => (
                                                        <span key={a} className="text-[9px] uppercase tracking-wider bg-amber-900/30 text-amber-400 px-1.5 py-0.5 rounded">
                                                            {a}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className={`font-display font-bold whitespace-nowrap ${terminato ? "text-elegant line-through" : "text-brand"}`}>€ {it.price.toFixed(2)}</div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                    )}
                </div>

                {/* Cart / current order */}
                <div className="bg-surface border border-[#1f2c44] rounded-lg flex flex-col">
                    <div className="p-5 border-b border-[#1f2c44]">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-brand">Comanda</div>
                        <div className="font-display text-2xl font-bold">Tavolo {table.number}</div>
                        <div className="text-xs text-elegant mt-1">Cameriere: {order.waiter_name}</div>
                        <button
                            onClick={() => {
                                setCoversInput(order.covers || table.seats || 2);
                                setCoversChildrenInput(order.covers_children || 0);
                                setCoverDisabledInput(!!order.cover_charge_disabled);
                                setCoverExtraInput(order.cover_charge_extra ? String(order.cover_charge_extra) : "");
                                setAddExtraCover(!!(order.cover_charge_extra && order.cover_charge_extra > 0));
                                setCoversPromptMode("edit");
                                setCoversPrompt(true);
                            }}
                            data-testid="covers-badge"
                            title="Imposta il numero di coperti"
                            className={`mt-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-bold uppercase tracking-wider transition-colors ${
                                (order.covers || 0) > 0
                                    ? "border-brand bg-brand-soft text-brand hover:bg-brand/20"
                                    : "border-amber-500 bg-amber-900/20 text-amber-300 hover:bg-amber-900/40 animate-pulse"
                            }`}
                        >
                            <Users className="w-3.5 h-3.5" />
                            {(order.covers || 0) > 0 ? (
                                <>
                                    {order.covers} adulti
                                    {order.covers_children > 0 && <> + {order.covers_children} bamb.</>}
                                    {coperto.enabled && !order.cover_charge_disabled && (
                                        <span className="ml-1 text-[10px] opacity-80">
                                            · € {(
                                                (coperto.unit * order.covers) +
                                                (coperto.childEnabled ? coperto.childUnit * (order.covers_children || 0) : 0)
                                            ).toFixed(2)}
                                        </span>
                                    )}
                                    {coperto.enabled && order.cover_charge_disabled && (
                                        <span className="ml-1 text-[10px] text-amber-300 font-bold">· senza coperto</span>
                                    )}
                                </>
                            ) : "Imposta coperti"}
                        </button>
                    </div>
                    {orderAlert.level !== "none" && (
                        <div className={`px-4 py-2 flex items-center gap-2 text-sm font-bold ${
                            orderAlert.level === "red" ? "bg-red-900/30 text-red-300 border-y border-red-700/50 animate-pulse" :
                            "bg-amber-900/30 text-amber-300 border-y border-amber-700/50"
                        }`} data-testid="order-wait-alert">
                            {orderAlert.level === "red" ? <AlarmClock className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                            Attesa cucina {orderAlert.maxMin}' — {orderAlert.level === "red" ? "controlla la cucina!" : "ritardo, monitora la comanda"}
                        </div>
                    )}
                    <div className="flex-1 p-3 space-y-2 max-h-[500px] overflow-y-auto" data-testid="order-items-list">
                        {visibleItems.length === 0 && (
                            <div className="text-elegant text-center py-10 text-sm">Nessun piatto. Tocca un prodotto per aggiungerlo.</div>
                        )}
                        {visibleItems.map((it) => {
                            const statusBadge =
                                it.status === "in_attesa" ? { label: "DA INVIARE", color: "bg-elegant/20 text-elegant" } :
                                it.status === "in_cucina" ? { label: "IN CUCINA · DA ACCETTARE", color: "bg-blue-900/40 text-blue-300 animate-pulse" } :
                                it.status === "in_preparazione" ? { label: "IN PREPARAZIONE", color: "bg-amber-900/40 text-amber-300" } :
                                it.status === "pronto" ? { label: "PRONTO — RITIRA", color: "bg-green-900/40 text-green-300 animate-pulse" } :
                                it.status === "consegnato" ? { label: "CONSEGNATO ✓", color: "bg-brand-soft text-brand" } :
                                { label: it.status?.toUpperCase() || "—", color: "bg-elegant/20 text-elegant" };
                            return (
                                <div key={it.id} className={`bg-surface-elevated border rounded-md p-3 ${
                                    it.status === "pronto" ? "border-green-600 ring-1 ring-green-600/30" :
                                    it.status === "consegnato" ? "border-brand/40 opacity-70" :
                                    "border-[#1f2c44]"
                                }`}>
                                    <div className="flex justify-between gap-2">
                                        <div className="flex-1">
                                            <div className="font-medium text-sm">{it.name}</div>
                                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                <span className="text-[10px] text-brand uppercase tracking-wider">{it.course}</span>
                                                <span className="text-[9px] bg-purple-900/50 text-purple-200 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider" data-testid={`item-round-${it.id}`}>
                                                    Segui {it.course_round || 1}
                                                </span>
                                                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold ${statusBadge.color}`}>
                                                    {statusBadge.label}
                                                </span>
                                            </div>
                                            {it.notes && <div className="text-[11px] italic text-amber-400 mt-1">📝 {it.notes}</div>}
                                            {it.extras?.length > 0 && (
                                                <div className="mt-1 space-y-0.5" data-testid={`item-extras-${it.id}`}>
                                                    {it.extras.map((ex) => (
                                                        <div key={ex.id} className="text-[11px] text-cyan-300 flex items-center gap-1">
                                                            <span className="font-bold">+</span>
                                                            <span>{ex.label}</span>
                                                            {ex.price > 0 && <span className="text-cyan-400 font-mono">· +€ {ex.price.toFixed(2)}</span>}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {it.allergens?.length > 0 && (
                                                <div className="flex items-center gap-1 mt-1 text-[10px] text-amber-400"><AlertTriangle className="w-3 h-3" />{it.allergens.join(", ")}</div>
                                            )}
                                            {it.delivered_at && (
                                                <div className="text-[10px] text-elegant mt-1">consegnato da {it.delivered_by}</div>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <div className="font-display text-brand font-bold">€ {itemGross(it).toFixed(2)}</div>
                                            {it.extras?.length > 0 && (
                                                <div className="text-[10px] text-cyan-400/70 mt-0.5">incl. extras</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between mt-2 gap-2">
                                        {it.status === "in_attesa" ? (
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => changeQty(it, -1)} className="w-8 h-8 rounded bg-surface-elevated hover:bg-[#1f2c44]" data-testid={`qty-minus-${it.id}`}><Minus className="w-3 h-3 mx-auto" /></button>
                                                <span className="font-display font-bold w-6 text-center">{it.quantity}</span>
                                                <button onClick={() => changeQty(it, 1)} className="w-8 h-8 rounded bg-surface-elevated hover:bg-[#1f2c44]" data-testid={`qty-plus-${it.id}`}><Plus className="w-3 h-3 mx-auto" /></button>
                                            </div>
                                        ) : <div className="text-xs text-elegant">×{it.quantity}</div>}
                                        {it.status === "in_attesa" && (
                                            <div className="flex gap-1.5 flex-wrap mt-1.5">
                                                <button onClick={() => { setNoteFor(it); setNoteText(it.notes || ""); }} className="h-9 px-3 rounded border border-elegant/40 text-[11px] text-elegant hover:bg-elegant/10 hover:text-warm uppercase tracking-wider font-bold transition-colors" data-testid={`note-${it.id}`}>📝 Note</button>
                                                <button onClick={() => setCourseFor(it)} className="h-9 px-3 rounded border border-elegant/40 text-[11px] text-elegant hover:bg-elegant/10 hover:text-warm uppercase tracking-wider font-bold transition-colors" data-testid={`course-${it.id}`}>Portata</button>
                                                <button onClick={() => setRoundFor(it)} className="h-9 px-3 rounded border border-purple-500/50 text-[11px] text-purple-200 hover:bg-purple-900/30 uppercase tracking-wider font-bold transition-colors" data-testid={`round-${it.id}`}>Segui {it.course_round || 1}</button>
                                                <button onClick={() => openExtras(it)} className="h-9 px-3 rounded border border-cyan-500/50 text-[11px] text-cyan-200 hover:bg-cyan-900/30 uppercase tracking-wider font-bold transition-colors" data-testid={`extras-${it.id}`}>
                                                    + Extra{it.extras?.length ? ` (${it.extras.length})` : ""}
                                                </button>
                                                <button onClick={() => removeItem(it.id)} className="h-9 w-9 rounded border border-red-500/40 text-red-400 hover:bg-red-900/20 flex items-center justify-center transition-colors" data-testid={`remove-${it.id}`} title="Rimuovi">
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}
                                        {it.status !== "in_attesa" && !it.paid_at && (
                                            <div className="flex gap-1.5 flex-wrap mt-1.5">
                                                <button onClick={() => openExtras(it)} className="h-9 px-3 rounded border border-cyan-500/50 text-[11px] text-cyan-200 hover:bg-cyan-900/30 uppercase tracking-wider font-bold transition-colors" data-testid={`extras-${it.id}`}>
                                                    + Extra{it.extras?.length ? ` (${it.extras.length})` : ""}
                                                </button>
                                                {canVoid && (
                                                    <button
                                                        onClick={() => openVoid(it)}
                                                        data-testid={`void-${it.id}`}
                                                        title="Elimina dal conto (solo cassa/admin, motivo obbligatorio)"
                                                        className="text-[10px] text-red-400 hover:text-red-200 uppercase tracking-wider font-bold flex items-center gap-1"
                                                    >
                                                        <X className="w-3 h-3" /> Annulla
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {it.status === "pronto" && (
                                            <button onClick={() => markDelivered(it)} data-testid={`deliver-${it.id}`}
                                                className="h-9 px-3 bg-brand hover:bg-brand-light text-deep font-bold uppercase tracking-wider text-[10px] rounded flex items-center gap-1">
                                                <CheckCircle2 className="w-3 h-3" /> Ho consegnato
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="p-5 border-t border-[#1f2c44] space-y-3">
                        <div className="flex justify-between text-elegant text-sm">
                            <span>Provvisorio</span>
                            <span className="font-display font-bold text-warm text-xl">€ {total.toFixed(2)}</span>
                        </div>
                        {/* ⚠️ Bottone Invia con STATO STAMPA persistente.
                            - idle:     verde brand (default)
                            - printing: spinner + "Stampa in corso..."
                            - ok:       verde scuro + ✓ "Stampato" (rimane 8s)
                            - warning:  GIALLO non bloccante (mixed) → "Trasmesso a video. Stampa fallita" + 2 bottoni Ristampa/Continua
                            - failed:   ROSSO LAMPEGGIANTE bloccante (print_only) → click ristampa obbligatoria */}
                        {printState === "warning" ? (
                            <div className="w-full" data-testid="print-warning-block">
                                <div className="bg-amber-500/15 border-2 border-amber-500 rounded-md p-3 mb-2 text-amber-200 text-xs flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                    <div>
                                        <div className="font-bold uppercase tracking-wider">Trasmessa a video</div>
                                        <div className="text-amber-100/80 text-[11px] mt-0.5">Stampa fallita: {lastPrintError}</div>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={reprintFailed} data-testid="reprint-warning-button"
                                        className="flex-1 h-12 bg-amber-500 text-deep font-bold uppercase tracking-wider rounded-md flex items-center justify-center gap-2 hover:bg-amber-400">
                                        <Printer className="w-4 h-4" /> Ristampa
                                    </button>
                                    <button onClick={dismissWarning} data-testid="continue-warning-button"
                                        className="flex-1 h-12 border-2 border-emerald-600 text-emerald-400 font-bold uppercase tracking-wider rounded-md flex items-center justify-center gap-2 hover:bg-emerald-600/10">
                                        <CheckCircle2 className="w-4 h-4" /> Continua
                                    </button>
                                </div>
                            </div>
                        ) : printState === "failed" ? (
                            <button
                                onClick={reprintFailed}
                                data-testid="reprint-failed-button"
                                className="w-full h-14 bg-red-600 text-white font-bold uppercase tracking-wider rounded-md flex items-center justify-center gap-2 animate-pulse hover:bg-red-700 transition"
                                title={lastPrintError || "Stampa non confermata — clicca per ristampare"}
                            >
                                <AlertOctagon className="w-5 h-5" /> Stampa fallita — Ristampa
                            </button>
                        ) : printState === "ok" ? (
                            <button
                                disabled
                                data-testid="send-kitchen-button-ok"
                                className="w-full h-14 bg-emerald-600 text-white font-bold uppercase tracking-wider rounded-md flex items-center justify-center gap-2"
                            >
                                <CheckCircle2 className="w-5 h-5" /> Stampato
                            </button>
                        ) : (
                            <button
                                onClick={sendToKitchen}
                                disabled={pendingCount === 0 || sending || printState === "printing"}
                                data-testid="send-kitchen-button"
                                className="w-full h-14 bg-brand text-deep font-bold uppercase tracking-wider rounded-md flex items-center justify-center gap-2 disabled:opacity-40"
                            >
                                {printState === "printing" ? (
                                    <>
                                        <Printer className="w-4 h-4 animate-pulse" /> Stampa in corso...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" /> {sending ? "Invio in corso..." : `Invia in cucina (${pendingCount})`}
                                    </>
                                )}
                            </button>
                        )}

                        <VoiceOrderPanel
                            orderId={order.id}
                            onItemsParsed={async (parsedItems) => {
                                // Aggiunge ogni item al carrello backend
                                for (const it of parsedItems) {
                                    try {
                                        if (it.matched_menu_item_id) {
                                            await api.post(`/orders/${order.id}/items`, {
                                                menu_item_id: it.matched_menu_item_id,
                                                quantity: it.quantity,
                                                notes: it.notes || "",
                                                course_round: it.course_round || 1,
                                                extras: it.extras || [],
                                            });
                                        } else if (it.name) {
                                            await api.post(`/orders/${order.id}/items/free`, {
                                                name: it.name,
                                                quantity: it.quantity,
                                                price: it.estimated_price || 0,
                                                course: "primo",
                                                department: "cucina",
                                                notes: it.notes || "",
                                            });
                                        }
                                    } catch (e) {
                                        toast.error(`Errore "${it.name}": ${formatError(e)}`);
                                    }
                                }
                                await reloadOrder();
                            }}
                        />
                        <button
                            onClick={requestBill}
                            data-testid="request-bill-button"
                            disabled={order.items.length === 0}
                            className="w-full h-12 border border-brand text-brand rounded-md flex items-center justify-center gap-2 hover:bg-brand/10 disabled:opacity-40"
                        >
                            <FileText className="w-4 h-4" /> Richiedi Conto
                        </button>
                    </div>
                </div>
            </div>

            {noteFor && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 max-w-md w-full">
                        <div className="font-display text-xl font-bold mb-2">{noteFor.name}</div>
                        <div className="text-xs text-elegant mb-4">Aggiungi note per la cucina</div>
                        <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="note-textarea"
                            className="w-full h-32 p-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none" placeholder="Es: senza cipolla, ben cotto..." />
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setNoteFor(null)} className="flex-1 h-12 border border-[#1f2c44] rounded-md text-elegant">Annulla</button>
                            <button onClick={saveNote} data-testid="save-note-button" className="flex-1 h-12 bg-brand text-deep font-bold rounded-md">Salva</button>
                        </div>
                    </div>
                </div>
            )}

            {courseFor && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 max-w-md w-full">
                        <div className="font-display text-xl font-bold mb-1">Portata</div>
                        <div className="text-xs text-elegant mb-4">Tipo di portata (antipasto, primo, secondo...)</div>
                        <div className="grid grid-cols-2 gap-2">
                            {COURSES.map((c) => (
                                <button key={c.id} onClick={() => setCourse(courseFor, c.id)} data-testid={`course-opt-${c.id}`}
                                    className={`h-12 rounded-md border ${courseFor.course === c.id ? "border-brand bg-brand/10 text-brand" : "border-[#1f2c44]"}`}>
                                    {c.label}
                                </button>
                            ))}
                        </div>
                        <button onClick={() => setCourseFor(null)} className="w-full h-12 mt-4 border border-[#1f2c44] rounded-md text-elegant">Chiudi</button>
                    </div>
                </div>
            )}

            {roundFor && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface border border-purple-700 rounded-lg p-6 max-w-md w-full">
                        <div className="font-display text-xl font-bold mb-1 text-purple-200">Segui</div>
                        <div className="text-xs text-elegant mb-4">
                            La cucina prepara prima il Segui 1, poi 2, poi 3.<br/>
                            I Segui successivi restano in attesa fino a che il precedente è stato consegnato.
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                                <button key={n} onClick={() => setRound(roundFor, n)} data-testid={`round-opt-${n}`}
                                    className={`h-14 rounded-md border font-display text-2xl font-bold ${((roundFor.course_round || 1) === n) ? "border-purple-400 bg-purple-900/40 text-purple-200" : "border-[#1f2c44] text-elegant hover:border-purple-500"}`}>
                                    {n}
                                </button>
                            ))}
                        </div>
                        <button onClick={() => setRoundFor(null)} className="w-full h-12 mt-4 border border-[#1f2c44] rounded-md text-elegant">Chiudi</button>
                    </div>
                </div>
            )}

            {extrasFor && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="extras-modal">
                    <div className="bg-surface border-2 border-cyan-500/70 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center gap-3 mb-3">
                            <Plus className="w-7 h-7 text-cyan-300" />
                            <div>
                                <div className="font-display text-xl font-bold">Aggiunte / Modifiche</div>
                                <div className="text-xs text-elegant">{extrasFor.name} · ×{extrasFor.quantity}</div>
                            </div>
                        </div>
                        <p className="text-[11px] text-elegant mb-3">
                            Tocca un preset per aggiungerlo subito, o compila i campi sotto per un&apos;aggiunta libera.<br/>
                            Il prezzo si somma a OGNI piatto della quantità (×{extrasFor.quantity}).
                        </p>

                        {/* Preset rapidi */}
                        <div className="mb-3" data-testid="extras-presets">
                            <div className="text-[10px] uppercase tracking-wider text-elegant mb-1.5">Preset rapidi</div>
                            <div className="grid grid-cols-2 gap-1.5">
                                {[
                                    { label: "Aggiunta formaggio", price: 1.5 },
                                    { label: "Aggiunta salumi", price: 2.0 },
                                    { label: "Aggiunta bufala", price: 3.0 },
                                    { label: "Aggiunta funghi", price: 1.5 },
                                    { label: "Aggiunta tonno", price: 2.0 },
                                    { label: "Doppia mozzarella", price: 2.0 },
                                    { label: "Senza cipolla", price: 0 },
                                    { label: "Senza glutine", price: 2.0 },
                                ].map((p, i) => {
                                    const already = extrasDraft.some((e) => e.label.toLowerCase() === p.label.toLowerCase());
                                    return (
                                        <button key={i}
                                            disabled={already || extrasDraft.length >= 10}
                                            onClick={() => {
                                                if (extrasDraft.length >= 10) return;
                                                setExtrasDraft((d) => [...d, { id: Math.random().toString(36).slice(2, 10), label: p.label, price: p.price }]);
                                            }}
                                            data-testid={`extra-preset-${i}`}
                                            className={`h-12 px-2 rounded border text-left transition-colors ${
                                                already
                                                    ? "border-cyan-500/20 bg-cyan-900/10 opacity-50 cursor-not-allowed"
                                                    : "border-cyan-500/40 bg-cyan-900/10 hover:bg-cyan-900/30 hover:border-cyan-400"
                                            }`}>
                                            <div className="text-[11px] font-bold text-warm leading-tight truncate">{p.label}</div>
                                            <div className="text-[10px] text-cyan-300 font-mono">
                                                {p.price > 0 ? `+€ ${p.price.toFixed(2)}` : "gratis"}
                                                {already && <span className="text-elegant ml-1">✓</span>}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Lista correnti */}
                        {extrasDraft.length > 0 && (
                            <div className="space-y-1 mb-3 max-h-40 overflow-y-auto" data-testid="extras-draft-list">
                                {extrasDraft.map((ex) => (
                                    <div key={ex.id} className="flex items-center justify-between bg-deep border border-[#1f2c44] rounded px-3 py-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-warm truncate">{ex.label}</div>
                                            {ex.price > 0 && <div className="text-xs text-cyan-300 font-mono">+€ {parseFloat(ex.price).toFixed(2)}</div>}
                                        </div>
                                        <button onClick={() => removeExtraFromDraft(ex.id)} data-testid={`extra-remove-${ex.id}`}
                                            className="text-red-400 hover:text-red-300 ml-2">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Form aggiunta libera */}
                        <div className="space-y-2 mb-3 pt-3 border-t border-[#1f2c44]">
                            <div className="text-[10px] uppercase tracking-wider text-elegant mb-0.5">Aggiunta libera</div>
                            <input
                                value={extraLabel}
                                onChange={(e) => setExtraLabel(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addExtraToDraft())}
                                placeholder="Nome dell'aggiunta (es. Doppio pomodoro)"
                                maxLength={80}
                                data-testid="extra-label-input"
                                className="w-full h-11 px-3 bg-deep border border-[#1f2c44] rounded text-warm focus:border-cyan-500 focus:outline-none text-sm"
                            />
                            <div className="flex gap-2">
                                <div className="flex-1 flex items-center bg-deep border border-[#1f2c44] rounded focus-within:border-cyan-500">
                                    <span className="px-3 text-elegant text-sm">€</span>
                                    <input
                                        type="number"
                                        step="0.10"
                                        min="0"
                                        value={extraPrice}
                                        onChange={(e) => setExtraPrice(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addExtraToDraft())}
                                        placeholder="0,00"
                                        data-testid="extra-price-input"
                                        className="flex-1 h-11 bg-transparent text-warm focus:outline-none text-sm font-mono"
                                    />
                                </div>
                                <button onClick={addExtraToDraft}
                                    data-testid="extra-add-btn"
                                    className="h-11 px-4 bg-cyan-600 hover:bg-cyan-500 text-white font-bold uppercase tracking-wider text-xs rounded">
                                    Aggiungi
                                </button>
                            </div>
                            <div className="text-[10px] text-elegant">Lascia il prezzo a 0 se la modifica è gratuita.</div>
                        </div>

                        <div className="flex gap-2 pt-3 border-t border-[#1f2c44]">
                            <button onClick={() => setExtrasFor(null)} data-testid="extras-cancel"
                                className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant">Annulla</button>
                            <button onClick={saveExtras} data-testid="extras-save"
                                className="flex-1 h-12 bg-brand hover:bg-brand-light text-deep font-bold rounded uppercase tracking-wider text-xs">
                                Salva ({extrasDraft.length})
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {voidFor && (
                <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4" data-testid="void-modal">
                    <div className="bg-surface border-2 border-red-500 rounded-lg p-6 max-w-md w-full">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-full bg-red-900/40 border border-red-500 flex items-center justify-center">
                                <X className="w-6 h-6 text-red-300" />
                            </div>
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-wider text-red-300">Annulla dal conto</div>
                                <div className="font-display text-xl font-bold">{voidFor.name}</div>
                                <div className="text-xs text-elegant">×{voidFor.quantity} · € {(voidFor.price * voidFor.quantity).toFixed(2)} · stato: {voidFor.status}</div>
                            </div>
                        </div>

                        <div className="bg-amber-900/20 border border-amber-500/50 rounded-md p-2 mb-4 text-xs text-amber-200 flex items-start gap-2">
                            <span className="font-bold">⚠</span>
                            <span>L'azione viene registrata nel log per audit. La cucina riceverà notifica via WebSocket.</span>
                        </div>

                        <div className="mb-3">
                            <div className="text-[10px] uppercase tracking-wider text-elegant mb-2 font-bold">Motivo (obbligatorio)</div>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                                {VOID_PRESETS.map((p) => (
                                    <button
                                        key={p}
                                        onClick={() => setVoidReason(p)}
                                        data-testid={`void-preset-${p.replace(/[^a-zA-Z0-9]/g,"-").toLowerCase()}`}
                                        className={`h-10 px-2 text-[11px] uppercase tracking-wider rounded border font-bold transition-colors ${
                                            voidReason === p
                                                ? "border-red-400 bg-red-900/40 text-red-100"
                                                : "border-[#1f2c44] text-elegant hover:border-red-500/60"
                                        }`}
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>
                            <input
                                value={voidReason}
                                onChange={(e) => setVoidReason(e.target.value)}
                                placeholder="…oppure scrivi il motivo"
                                maxLength={200}
                                data-testid="void-reason-input"
                                className="w-full h-11 px-3 bg-deep border border-[#1f2c44] rounded text-warm focus:border-red-500 focus:outline-none text-sm"
                            />
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={() => { setVoidFor(null); setVoidReason(""); }}
                                data-testid="void-cancel"
                                disabled={voidBusy}
                                className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant disabled:opacity-50"
                            >
                                Mantieni
                            </button>
                            <button
                                onClick={confirmVoid}
                                disabled={voidBusy || !voidReason.trim()}
                                data-testid="void-confirm"
                                className="flex-1 h-12 bg-red-700 hover:bg-red-600 disabled:bg-red-900/30 disabled:cursor-not-allowed text-white font-bold rounded uppercase tracking-wider text-xs flex items-center justify-center gap-2"
                            >
                                <X className="w-4 h-4" /> {voidBusy ? "Elimino..." : "Elimina"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {coversPrompt && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="covers-modal">
                    <div className="bg-surface border-2 border-brand rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center gap-3 mb-3">
                            <Users className="w-7 h-7 text-brand" />
                            <div>
                                <div className="font-display text-2xl font-bold">Quanti coperti?</div>
                                <div className="text-xs text-elegant">Tavolo {table.number} · {table.seats} posti</div>
                            </div>
                        </div>
                        <div className="text-xs text-elegant mb-4">
                            {coperto.enabled
                                ? <>Coperto adulto <strong className="text-brand text-base">€ {coperto.unit.toFixed(2)}</strong>{coperto.childEnabled && <> · Coperto bambino <strong className="text-brand">€ {coperto.childUnit.toFixed(2)}</strong></>}. Verrà aggiunto al conto in cassa.</>
                                : "Inserisci il numero di persone al tavolo. Il menu attivo non prevede coperto."}
                        </div>

                        {/* Adulti */}
                        <div className="mb-4">
                            <div className="text-[11px] uppercase tracking-wider text-elegant mb-2 font-bold">Adulti</div>
                            <div className="grid grid-cols-4 gap-2 mb-2">
                                {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => (
                                    <button key={n} onClick={() => setCoversInput(n)} data-testid={`covers-quick-${n}`}
                                        className={`h-12 rounded-md border font-display text-xl font-bold transition-colors ${
                                            coversInput === n
                                                ? "border-brand bg-brand/20 text-brand"
                                                : "border-[#1f2c44] text-elegant hover:border-brand/60"
                                        }`}>
                                        {n}
                                    </button>
                                ))}
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] uppercase tracking-wider text-elegant whitespace-nowrap">Personalizzato</span>
                                <input type="number" min="1" max="200" value={coversInput}
                                    onChange={(e) => setCoversInput(e.target.value)}
                                    data-testid="covers-input"
                                    className="flex-1 h-10 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-right font-display text-lg focus:border-brand focus:outline-none" />
                            </div>
                        </div>

                        {/* Bambini (solo se previsti dal menu) */}
                        {coperto.childEnabled && (
                            <div className="mb-4 pt-4 border-t border-[#1f2c44]">
                                <div className="text-[11px] uppercase tracking-wider text-amber-300 mb-2 font-bold flex items-center gap-1">
                                    👶 Bambini <span className="text-elegant text-[10px]">(coperto ridotto € {coperto.childUnit.toFixed(2)})</span>
                                </div>
                                <div className="grid grid-cols-4 gap-2 mb-2">
                                    {[0, 1, 2, 3, 4, 5, 6, 8].map((n) => (
                                        <button key={n} onClick={() => setCoversChildrenInput(n)} data-testid={`covers-child-quick-${n}`}
                                            className={`h-12 rounded-md border font-display text-xl font-bold transition-colors ${
                                                parseInt(coversChildrenInput, 10) === n
                                                    ? "border-amber-500 bg-amber-900/30 text-amber-200"
                                                    : "border-[#1f2c44] text-elegant hover:border-amber-500/60"
                                            }`}>
                                            {n}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] uppercase tracking-wider text-elegant whitespace-nowrap">Personalizzato</span>
                                    <input type="number" min="0" max="200" value={coversChildrenInput}
                                        onChange={(e) => setCoversChildrenInput(e.target.value)}
                                        data-testid="covers-child-input"
                                        className="flex-1 h-10 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-right font-display text-lg focus:border-amber-500 focus:outline-none" />
                                </div>
                            </div>
                        )}

                        {/* Toggle: Senza coperto (es. cliente che prende solo caffè) */}
                        {coperto.enabled && (
                            <label className="flex items-center gap-3 bg-deep border border-[#1f2c44] rounded-md px-3 py-2 mb-4 cursor-pointer hover:border-amber-500/60 select-none"
                                data-testid="cover-disabled-row">
                                <input
                                    type="checkbox"
                                    checked={coverDisabledInput}
                                    onChange={(e) => setCoverDisabledInput(e.target.checked)}
                                    data-testid="cover-disabled-toggle"
                                    className="w-5 h-5 accent-amber-500"
                                />
                                <div className="flex-1">
                                    <div className="text-sm text-warm font-bold">Non addebitare il coperto</div>
                                    <div className="text-[10px] text-elegant">Es. solo caffè/aperitivo, gruppo che condivide, cliente abituale…</div>
                                </div>
                            </label>
                        )}

                        {/* Aggiungi coperto opzionale (es. pranzo alla carta) — visibile solo se menu attivo NON prevede coperto */}
                        {!coperto.enabled && (
                            <div className="mb-4 pt-4 border-t border-[#1f2c44]">
                                <label className="flex items-center gap-3 bg-deep border border-[#1f2c44] rounded-md px-3 py-2 cursor-pointer hover:border-brand/60 select-none"
                                    data-testid="cover-extra-row">
                                    <input
                                        type="checkbox"
                                        checked={addExtraCover}
                                        onChange={(e) => setAddExtraCover(e.target.checked)}
                                        data-testid="cover-extra-toggle"
                                        className="w-5 h-5 accent-brand"
                                    />
                                    <div className="flex-1">
                                        <div className="text-sm text-warm font-bold">Aggiungi coperto su questo tavolo</div>
                                        <div className="text-[10px] text-elegant">Es. pranzo alla carta, servizio extra, evento dedicato</div>
                                    </div>
                                </label>
                                {addExtraCover && (
                                    <div className="mt-3 flex items-center gap-2">
                                        <span className="text-[10px] uppercase tracking-wider text-elegant whitespace-nowrap">Coperto €/pers</span>
                                        <input type="number" min="0" max="50" step="0.50"
                                            value={coverExtraInput}
                                            placeholder="es. 2,00"
                                            onChange={(e) => setCoverExtraInput(e.target.value)}
                                            data-testid="cover-extra-input"
                                            className="flex-1 h-10 px-3 bg-surface-elevated border border-brand/40 rounded text-right font-display text-lg focus:border-brand focus:outline-none" />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Anteprima totale coperto */}
                        {coperto.enabled && coversInput > 0 && !coverDisabledInput && (
                            <div className="bg-deep border border-brand/30 rounded-md p-3 mb-4 space-y-1">
                                <div className="flex justify-between text-xs text-elegant">
                                    <span>Adulti × {coversInput}</span>
                                    <span>€ {(coperto.unit * coversInput).toFixed(2)}</span>
                                </div>
                                {coperto.childEnabled && parseInt(coversChildrenInput, 10) > 0 && (
                                    <div className="flex justify-between text-xs text-amber-300">
                                        <span>Bambini × {coversChildrenInput}</span>
                                        <span>€ {(coperto.childUnit * parseInt(coversChildrenInput, 10)).toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between items-center pt-1 border-t border-[#1f2c44]">
                                    <span className="text-xs text-elegant">Coperto totale</span>
                                    <span className="font-display text-xl font-bold text-brand">
                                        € {((coperto.unit * coversInput) + (coperto.childEnabled ? coperto.childUnit * parseInt(coversChildrenInput, 10) : 0)).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Anteprima coperto extra (menu senza coperto + opzione attivata) */}
                        {!coperto.enabled && addExtraCover && parseFloat((coverExtraInput || "0").replace(",", ".")) > 0 && coversInput > 0 && (
                            <div className="bg-deep border border-brand/30 rounded-md p-3 mb-4" data-testid="cover-extra-preview">
                                <div className="flex justify-between items-center">
                                    <span className="text-xs text-elegant">Coperto totale (extra)</span>
                                    <span className="font-display text-xl font-bold text-brand">
                                        € {(parseFloat((coverExtraInput || "0").replace(",", ".")) * coversInput).toFixed(2)}
                                    </span>
                                </div>
                                <div className="text-[10px] text-elegant text-right">{coversInput} × € {parseFloat((coverExtraInput || "0").replace(",", ".")).toFixed(2)}</div>
                            </div>
                        )}

                        {coperto.enabled && coverDisabledInput && (
                            <div className="bg-amber-900/20 border border-amber-500/50 rounded-md p-3 mb-4 text-xs text-amber-200 flex items-start gap-2" data-testid="cover-disabled-banner">
                                <span className="font-bold">⚠</span>
                                <span>Il coperto NON verrà addebitato al tavolo. Il cassiere vedrà la nota in cassa.</span>
                            </div>
                        )}

                        <div className="flex gap-2">
                            {coversPromptMode === "opening" ? (
                                <button onClick={cancelTableOpening}
                                    data-testid="covers-cancel-opening"
                                    className="flex-1 h-12 border border-red-500/60 text-red-300 hover:bg-red-900/20 rounded-md uppercase tracking-wider text-xs font-bold transition-colors">
                                    Annulla apertura
                                </button>
                            ) : (
                                <button onClick={() => setCoversPrompt(false)}
                                    data-testid="covers-cancel"
                                    className="flex-1 h-12 border border-[#1f2c44] text-elegant hover:border-elegant rounded-md uppercase tracking-wider text-xs font-bold transition-colors">
                                    Annulla
                                </button>
                            )}
                            <button onClick={saveCovers}
                                data-testid="covers-save"
                                className="flex-[1.4] h-12 bg-brand text-deep font-bold rounded-md uppercase tracking-wider">
                                Conferma
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderEditor;

// ============ VOICE ORDER PANEL ============
const VoiceOrderPanel = ({ orderId, onItemsParsed }) => {
    const [enabled, setEnabled] = React.useState(() => typeof window !== "undefined" && localStorage.getItem("voice_order_enabled") === "1");
    const [recording, setRecording] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [lastTranscript, setLastTranscript] = React.useState("");
    const [draft, setDraft] = React.useState([]); // items pronti per essere inviati al backend
    const mediaRecorderRef = React.useRef(null);
    const chunksRef = React.useRef([]);

    React.useEffect(() => {
        const onChange = (e) => setEnabled(!!e.detail);
        window.addEventListener("voice_order_changed", onChange);
        return () => window.removeEventListener("voice_order_changed", onChange);
    }, []);

    if (!enabled) return null;

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
            const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
            mediaRecorderRef.current = mr;
            chunksRef.current = [];
            mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
            mr.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
                if (blob.size === 0) { toast.error("Audio vuoto"); return; }
                setBusy(true);
                const fd = new FormData();
                fd.append("file", blob, "voice.webm");
                try {
                    const { data } = await api.post("/voice/transcribe-order", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    setLastTranscript(data.transcript || "");
                    if (data.items && data.items.length > 0) {
                        setDraft((prev) => [...prev, ...data.items]);
                        toast.success(`${data.items.length} piatti dalla voce`);
                    } else {
                        toast.warning(data.warning || "Nessun piatto riconosciuto");
                    }
                } catch (e) { toast.error(formatError(e)); }
                setBusy(false);
            };
            mr.start();
            setRecording(true);
        } catch (e) {
            toast.error("Microfono non disponibile: " + (e?.message || "permesso negato"));
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && recording) {
            mediaRecorderRef.current.stop();
            setRecording(false);
        }
    };

    const speakRecap = () => {
        if (!("speechSynthesis" in window)) { toast.error("TTS non supportato"); return; }
        if (draft.length === 0) { toast.info("Nessuna comanda nella bozza"); return; }
        const synth = window.speechSynthesis;
        synth.cancel();
        const text = "Riepilogo comanda. " + draft.map((it) => {
            const ex = (it.extras || []).filter((e) => e.label).map((e) => e.label).join(", ");
            const segui = it.course_round && it.course_round > 1 ? ` Segui ${it.course_round}.` : "";
            const notes = it.notes ? ` Note: ${it.notes}.` : "";
            return `${it.quantity} ${it.name}.${ex ? " Aggiunte: " + ex + "." : ""}${notes}${segui}`;
        }).join(" ");
        const u = new SpeechSynthesisUtterance(text);
        u.lang = "it-IT";
        u.rate = 1.0;
        synth.speak(u);
    };

    const sendDraftToOrder = async () => {
        if (draft.length === 0) return;
        setBusy(true);
        await onItemsParsed(draft);
        setDraft([]);
        setLastTranscript("");
        setBusy(false);
        toast.success("Comanda aggiunta al tavolo");
    };

    return (
        <div className="mt-3 p-3 bg-purple-950/30 border-2 border-purple-500/60 rounded-md" data-testid="voice-order-panel">
            <div className="flex items-center gap-2 mb-2">
                <Mic className="w-4 h-4 text-purple-300" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300 font-bold">Comanda vocale (AI)</div>
            </div>
            {recording ? (
                <button onClick={stopRecording} data-testid="voice-stop-btn"
                    className="w-full h-14 bg-red-600 hover:bg-red-500 text-white font-bold rounded uppercase tracking-wider flex items-center justify-center gap-2 animate-pulse">
                    <Square className="w-5 h-5" /> Premi per terminare
                </button>
            ) : (
                <button onClick={startRecording} disabled={busy} data-testid="voice-record-btn"
                    className="w-full h-14 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-bold rounded uppercase tracking-wider flex items-center justify-center gap-2">
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mic className="w-5 h-5" />}
                    {busy ? "Elaborazione…" : (draft.length > 0 ? "+ Aggiungi altra comanda" : "Comanda vocale")}
                </button>
            )}
            {lastTranscript && (
                <div className="mt-2 text-[11px] text-purple-200 italic" data-testid="voice-transcript">&ldquo;{lastTranscript}&rdquo;</div>
            )}
            {draft.length > 0 && (
                <div className="mt-3 space-y-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-purple-300">Bozza ({draft.length} piatti)</div>
                    {draft.map((it, i) => (
                        <div key={i} className="text-xs bg-deep border border-purple-500/30 rounded px-2 py-1.5 flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                                <span className="font-bold text-warm">{it.quantity}× {it.name}</span>
                                {(it.estimated_price > 0 || it.matched_menu_item_id) && (
                                    <span className="ml-1 text-[10px] text-brand font-mono">€ {(it.estimated_price || 0).toFixed(2)}</span>
                                )}
                                {(it.course_round || 0) > 1 && <span className="ml-1 text-[10px] text-purple-300">Segui {it.course_round}</span>}
                                {!it.matched_menu_item_id && it.estimated_price > 0 && (
                                    <span className="ml-1 text-[9px] text-amber-300" title={it.macro_category || ""}>
                                        ~stimato{it.macro_category ? ` (${it.macro_category.toLowerCase().replace(/_/g, " ")})` : ""}
                                    </span>
                                )}
                                {!it.matched_menu_item_id && !it.estimated_price && <span className="ml-1 text-[9px] text-red-300">⚠ no match</span>}
                                {(it.extras || []).length > 0 && (
                                    <div className="text-[10px] text-cyan-300">+ {it.extras.map((e) => e.label + (e.price > 0 ? ` €${e.price.toFixed(2)}` : "")).join(", ")}</div>
                                )}
                                {it.notes && <div className="text-[10px] text-elegant italic">📝 {it.notes}</div>}
                            </div>
                            <button onClick={() => setDraft(draft.filter((_, j) => j !== i))}
                                className="text-red-400 hover:text-red-200" title="Rimuovi">
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                    <div className="grid grid-cols-2 gap-2 pt-1">
                        <button onClick={speakRecap} data-testid="voice-verify-btn"
                            className="h-11 bg-amber-600 hover:bg-amber-500 text-white font-bold rounded uppercase tracking-wider text-xs flex items-center justify-center gap-1">
                            <Volume2 className="w-4 h-4" /> Verifica
                        </button>
                        <button onClick={sendDraftToOrder} disabled={busy} data-testid="voice-send-draft-btn"
                            className="h-11 bg-brand hover:bg-brand-light text-deep font-bold rounded uppercase tracking-wider text-xs flex items-center justify-center gap-1 disabled:opacity-50">
                            <CheckCircle2 className="w-4 h-4" /> Aggiungi al tavolo
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
